<template>
  <v-card>
    <v-app-bar flat dark color="primary">
      <v-toolbar-title>
        <strong>Profile:</strong>
        <span class="font-weight-regular">
          {{ getNameFromUser(user) || user.email }}
        </span>
      </v-toolbar-title>

      <v-spacer />
      <v-btn
        small
        :color="currentUser.disabled ? 'success' : 'error'"
        :loading="loadingDisable"
        @click="setUserDisabled(user.id || user.uid, !currentUser.disabled)"
        >{{ currentUser.disabled ? 'Enable user' : 'Disable user' }}</v-btn
      >
    </v-app-bar>
    <v-card-text class="pt-3">
      <div class="d-flex pb-3">
        <div class="content">
          <v-list-item v-for="item in userInfo" :key="item.desc" class="mt-3">
            <v-list-item-avatar>
              <v-icon>{{ item.icon }}</v-icon>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-subtitle class="grey--text">{{
                item.desc
              }}</v-list-item-subtitle>
              <v-list-item-title>{{ item.value }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </div>
        <img
          v-if="user.avatar_url"
          class="profile__avatar"
          :src="user.avatar_url"
          :alt="getNameFromUser(user)"
        />
      </div>
      <v-btn
        v-if="!history.length"
        dark
        small
        color="success"
        :loading="loadingBilling"
        @click="getPaymentInfo"
      >
        Get Billing History
        <v-icon right>keyboard_arrow_down</v-icon>
      </v-btn>
      <template v-else>
        <v-list rounded>
          <v-subheader>BILLING HISTORY</v-subheader>
          <v-list-item-group color="primary">
            <v-list-item
              v-for="(item, i) in history"
              :key="i"
              :href="item.invoice_pdf"
              target="_blank"
            >
              <v-list-item-icon>
                <v-icon>cloud_download</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title
                  class="subtitle-1"
                  v-text="item.number"
                ></v-list-item-title>
                <v-list-item-subtitle class="subtitle-2">
                  Date: {{ formatDate(item.date) }}
                  <span :class="item.paid ? 'green--text' : 'red--text'"
                    >- {{ item.paid ? 'Paid' : 'Unpaid' }}</span
                  >
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list-item-group>
        </v-list>
        <v-btn v-if="hasMore" dark small color="success" @click="getPaymentInfo"
          >More</v-btn
        >
      </template>
    </v-card-text>
    <v-card-actions class="pt-2">
      <v-spacer />
      <v-btn color="primary" @click="$emit('close')">Close</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { fbfunctions } from '@/firebaseConfig';
import getNameFromUser from '@/helpers/getNameFromUser.js';
import moment from 'moment';

export default {
  name: 'ViewProfile',
  props: {
    user: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      history: [],
      hasMore: true,
      range: [],
      loadingBilling: false,
      loadingDisable: false,
      currentUser: {}
    };
  },
  computed: {
    userInfo() {
      let user = this.user;
      let userInfo = [];
      if (user.email) {
        userInfo.push({
          desc: 'Email',
          value: user.email,
          icon: 'email'
        });
      }
      if (user.business) {
        if (user.business.name) {
          userInfo.push({
            desc: 'Business Name',
            value: user.business.name,
            icon: 'business'
          });
        }
        if (user.business.business_type) {
          userInfo.push({
            desc: 'Business Type',
            value: user.business.business_type,
            icon: 'business_center'
          });
        }
      }
      if (user.card) {
        userInfo.push({
          desc: 'Registered Card',
          value: `**** **** **** ${user.card.last4}`,
          icon: 'credit_card'
        });
      }
      if (user.subscription && user.subscription.status) {
        userInfo.push({
          desc: 'Subscription Status',
          value: user.subscription.status,
          icon: 'assignment_turned_in'
        });
      } else if (user.subscription_status) {
        userInfo.push({
          desc: 'Subscription Status',
          value: user.subscription_status,
          icon: 'assignment_turned_in'
        });
      }

      if (user.signup_referral_code) {
        userInfo.push({
          desc: 'Referral Code',
          value: user.signup_referral_code,
          icon: 'loyalty'
        });
      }
      return userInfo;
    }
  },
  mounted() {
    this.currentUser = this.user;
  },
  methods: {
    formatDate(timestamp) {
      return moment(timestamp, 'X').format('YYYY-MM-DD');
    },
    getNameFromUser,
    getPaymentInfo() {
      this.loadingBilling = true;
      let getPaymentInfo = fbfunctions.httpsCallable('getBillingHistory');
      let customer_id = this.user.stripe_id;
      let limit = 5;
      let lastItem = this.history.length || null;
      let starting_after = lastItem ? this.history[lastItem - 1].id : null;

      getPaymentInfo({ customer_id, limit, starting_after })
        .then(res => {
          this.history.push(...res.data.data);
          if (!res.data.has_more) this.hasMore = false;
        })
        .catch(error => {
          this.$store.dispatch('createSnackbar', {
            color: 'error',
            text: `Error adding room: ${error}`
          });
        })
        .finally(() => {
          this.loadingBilling = false;
        });
    },
    async setUserDisabled(uid, disabled) {
      this.loadingDisable = true;

      let setUserDisabled = fbfunctions.httpsCallable('setUserDisabled');
      let payload = {
        uid,
        disabled
      };
      try {
        let res = await setUserDisabled(payload);
        this.currentUser = res.data;
      } catch (error) {
        let statusPrefix = disabled ? 'dis' : 'en';
        this.$store.dispatch('createSnackbar', {
          color: 'error',
          text: `Error ${statusPrefix}abling user: ${error}`
        });
      } finally {
        this.loadingDisable = false;
      }
    }
  }
};
</script>

<style lang="scss">
.profile {
  &__avatar {
    max-width: 130px;
    max-height: 130px;
  }
}
</style>
